import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

type Cmd = {
    id: string;
    label: string;
    to: string;
    keywords?: string;
    section?: string;
};

export default function CommandPalette({
    projectId,
    weekOf,
    open,
    onClose,
}: {
    projectId?: string;
    weekOf?: string;
    open: boolean;
    onClose: () => void;
}) {
    const [q, setQ] = useState('');
    const nav = useNavigate();
    const loc = useLocation();

    // 단축키 토글
    useEffect(() => {
        const h = (e: KeyboardEvent) => {
            const isK = e.key.toLowerCase() === 'k';
            const mod = e.ctrlKey || e.metaKey;
            if (isK && mod) {
                e.preventDefault();
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                open ? onClose() : null;
            }
        };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [open, onClose]);

    const cmds: Cmd[] = useMemo(
        () => [
            { id: 'wbs', label: 'WBS Editor', to: `/projects/${projectId}/wbs`, section: 'Pages' },
            { id: 'tasks', label: 'Tasks', to: `/projects/${projectId}/tasks`, section: 'Pages' },
            { id: 'risks', label: 'Risks', to: `/projects/${projectId}/risks`, section: 'Pages' },
            {
                id: 'gantt',
                label: 'Gantt Chart',
                to: `/projects/${projectId}/gantt-chart${weekOf ? `?weekOf=${weekOf}` : ''}`,
                section: 'Pages',
            },
            { id: 'dashboard', label: 'Dashboard', to: `/projects/${projectId}/dashboard`, section: 'Pages' },

            // Tools / Actions (예시)
            {
                id: 'approvals',
                label: 'Open Approval Queue',
                to: `${loc.pathname}#open-approvals`,
                section: 'Tools',
                keywords: 'approval ev pending',
            },
            { id: 'newTask', label: 'New Task…', to: `${loc.pathname}#new-task`, section: 'Tools' },
        ],
        [projectId, weekOf, loc.pathname],
    );

    const list = useMemo(() => {
        const qnorm = q.trim().toLowerCase();
        if (!qnorm) return cmds;
        return cmds.filter((c) => c.label.toLowerCase().includes(qnorm) || c.keywords?.toLowerCase().includes(qnorm));
    }, [cmds, q]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[120] bg-black/30" onClick={onClose}>
            <div
                className="mx-auto mt-24 w-full max-w-xl rounded-2xl bg-white shadow-xl border"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="border-b p-2">
                    <input
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Quick search… (type to filter, ↑/↓, Enter)"
                        className="w-full bg-transparent outline-none px-3 py-2 text-sm"
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') onClose();
                        }}
                    />
                </div>

                <ul className="max-h-[50vh] overflow-auto py-2">
                    {list.map((c) => (
                        <li key={c.id} className="px-2">
                            <button
                                onClick={() => {
                                    onClose();
                                    nav(c.to);
                                }}
                                className="w-full text-left px-3 py-2 rounded hover:bg-gray-50"
                            >
                                <div className="text-sm">{c.label}</div>
                                <div className="text-[11px] text-gray-500">{c.section}</div>
                            </button>
                        </li>
                    ))}
                    {list.length === 0 && <li className="px-3 py-6 text-sm text-gray-500">No results</li>}
                </ul>
            </div>
        </div>
    );
}
